import { useMemo } from 'react';
import {
    Box,
    VStack,
    HStack,
    Divider,
    Heading,
    Tooltip,
    Text,
    StatNumber,
    Stat,
    Button,
    SimpleGrid,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { QuestionIcon, ChevronUpIcon } from '@chakra-ui/icons';
import useIsMobile from '../hooks/useIsMobile.js';
import PDFRender from "../helpers/PDFRender";
import { CSVLink } from "react-csv";
import { parameterTextConverter } from "../helpers/parameterTextConverter";
import { unitSelectorPDF } from "../helpers/unitSelectorPDF";
import { STATUS, STATUS_LABEL, STATUS_BG_COLOR } from '../constants/status.js';

const TOOLTIP_TEXT = (
    <Text align='justify'>
        Indica si los sensores (independientemente del
        parámetro que midan) registran o no cantidades en
        los rangos adecuados para la salud de las personas.
        Siendo "Bueno" el mejor estado y "Emergencia" el peor.
    </Text>
);

const STATUS_ORDER = [
    STATUS.BUENO,
    STATUS.REGULAR,
    STATUS.ALERTA,
    STATUS.PRE_EMERGENCIA,
    STATUS.EMERGENCIA,
    STATUS.NO_DISPONIBLE,
];

// Previene CSV/formula injection: Excel interpreta celdas que empiezan con =, +, -, @, |, % como fórmulas
const sanitizeCSVField = (value) => {
    const str = String(value ?? '');
    return /^[=+\-@|%]/.test(str) ? `'${str}` : str;
};

const StatusSideBar = (props) => {

    const { isOpen, onOpen, onClose } = useDisclosure();
    const isMobile = useIsMobile();
    const toast = useToast();

    const counts = useMemo(() => {
        const result = Object.fromEntries(STATUS_ORDER.map(s => [s, 0]));
        for (const station of props.data) {
            for (const sensor of station.realtime) {
                const code = sensor.tableRow.statuscode;
                if (code in result) result[code]++;
            }
        }
        return result;
    }, [props.data]);

    const total = useMemo(() => Object.values(counts).reduce((s, n) => s + n, 0), [counts]);

    const dataCSV = useMemo(() => {
        const rows = [["Estación", "Parámetro", "Fecha", "Estado", "Valor", "Unidad"]];
        for (const station of props.data) {
            for (const sensor of station.realtime) {
                rows.push([
                    sanitizeCSVField(station.nombre),
                    sanitizeCSVField(parameterTextConverter(sensor.tableRow.parameter)),
                    sanitizeCSVField(sensor.tableRow.datetime),
                    sanitizeCSVField(sensor.tableRow.status),
                    sanitizeCSVField(sensor.tableRow.value),
                    sanitizeCSVField(unitSelectorPDF(sensor.tableRow.parameter)),
                ]);
            }
        }
        return rows;
    }, [props.data]);

    const dateStr = (props.lastUpdated ?? new Date()).toISOString().slice(0, 10);

    const lastUpdatedStr = props.lastUpdated
        ? props.lastUpdated.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
        : null;

    const statsContent = (
        <VStack spacing={4} align='stretch'>
            {STATUS_ORDER.map(code => (
                <HStack key={code} borderRadius='7px' bg={STATUS_BG_COLOR[code]}>
                    <Box h='40px' w='80%' p={2}>{STATUS_LABEL[code]}</Box>
                    <Box h='40px' w='20%' display='flex' alignItems='center'>
                        <Stat><StatNumber>{counts[code]}</StatNumber></Stat>
                    </Box>
                </HStack>
            ))}
            <HStack justify='space-between'>
                <Text fontSize='sm' color='gray.700'>Total sensores: {total}</Text>
                {lastUpdatedStr && (
                    <Text fontSize='xs' color='gray.600'>Actualizado: {lastUpdatedStr}</Text>
                )}
            </HStack>
            <Divider />
            <Heading size='md'>Descargar información</Heading>
            <SimpleGrid columns={2} spacing={3}>
                <Button colorScheme='blue' onClick={() => PDFRender(props.data, props.lastUpdated, () =>
                    toast({ title: 'Error al generar el PDF', description: 'Intenta nuevamente.', status: 'error', duration: 4000, isClosable: true })
                )}>
                    PDF
                </Button>
                <CSVLink data={dataCSV} filename={`report_${dateStr}.csv`}>
                    <Button width='100%' colorScheme='green'>CSV</Button>
                </CSVLink>
            </SimpleGrid>
        </VStack>
    );

    if (isMobile) {
        return (
            <>
                <Box
                    position='fixed'
                    bottom='0'
                    left='0'
                    right='0'
                    zIndex={1001}
                    bg='gray.400'
                    borderTop='2px solid'
                    borderColor='gray.500'
                    py={2}
                    px={4}
                    cursor='pointer'
                    onClick={onOpen}
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                    gap={2}
                >
                    <ChevronUpIcon boxSize={5} />
                    <Text fontSize='sm' fontWeight='bold'>Estado de sensores</Text>
                    <ChevronUpIcon boxSize={5} />
                </Box>

                <Drawer placement='bottom' isOpen={isOpen} onClose={onClose}>
                    <DrawerOverlay />
                    <DrawerContent borderTopRadius='16px' maxH='85vh' bg='gray.400'>
                        <DrawerCloseButton />
                        <DrawerHeader>
                            <HStack spacing={2} align='center'>
                                <Text>Estado general de los sensores</Text>
                                <Tooltip label={TOOLTIP_TEXT}>
                                    <QuestionIcon mb='2px' flexShrink={0} />
                                </Tooltip>
                            </HStack>
                        </DrawerHeader>
                        <DrawerBody overflowY='auto' pb={6}>
                            {statsContent}
                        </DrawerBody>
                    </DrawerContent>
                </Drawer>
            </>
        );
    }

    return (
        <VStack margin='15px' spacing={4} align='stretch'>
            <HStack spacing={2} align='center'>
                <Heading size='md'>Estado general de los sensores</Heading>
                <Tooltip label={TOOLTIP_TEXT}>
                    <QuestionIcon mb='2px' flexShrink={0} />
                </Tooltip>
            </HStack>
            {statsContent}
        </VStack>
    );
};

export default StatusSideBar;
